import React, { useState, useEffect, useCallback } from 'react'
import { lumi } from '../lib/lumi'
import {Plus, Search, Edit2, Trash2, Eye, EyeOff, X, Globe, Phone, Mail, MapPin, Image, Star, ChevronDown, ChevronUp, Save, RefreshCw} from 'lucide-react'

interface SignatureDrink {
  name: string
  description?: string
}

interface Partner {
  _id: string
  companyName: string
  companyNameEn?: string
  slug?: string
  contactName: string
  email: string
  phone?: string
  taxId?: string
  address?: {
    full?: string
    district?: string
    street?: string
  }
  type: 'wholesale' | 'oem' | 'supplier' | 'distributor'
  creditTerms?: {
    paymentTerms?: 'NET7' | 'NET15' | 'NET30' | 'NET45' | 'PREPAID'
    creditLimit?: number
    currency?: 'TWD' | 'USD' | 'CNY'
  }
  specialProducts?: string[]
  signatureDrinks?: SignatureDrink[]
  story?: string
  storyEn?: string
  signatureDrinksEn?: SignatureDrinkEn[]
  logo?: string
  images?: string[]
  isPublic?: boolean
  status: 'active' | 'pending' | 'suspended' | 'inactive'
  notes?: string
  creator?: string
  createdAt: string
  updatedAt?: string
}

const TYPE_LABELS: Record<string, string> = {
  wholesale: '批發商',
  oem: 'OEM 代工',
  supplier: '供應商',
  distributor: '經銷商',
}

const STATUS_LABELS: Record<string, string> = {
  active: '合作中',
  pending: '審核中',
  suspended: '暫停',
  inactive: '停用',
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-600',
  pending: 'bg-yellow-600',
  suspended: 'bg-orange-600',
  inactive: 'bg-gray-600',
}

const PAYMENT_TERMS = ['NET7', 'NET15', 'NET30', 'NET45', 'PREPAID']

// ── 空白表單預設值 ──────────────────────────────────────────────
const emptyForm = (): Partial<Partner> => ({
  companyName: '',
  companyNameEn: '',
  slug: '',
  storyEn: '',
  signatureDrinksEn: [],
  contactName: '',
  email: '',
  phone: '',
  taxId: '',
  address: { full: '', district: '', street: '' },
  type: 'wholesale',
  creditTerms: { paymentTerms: 'NET30', creditLimit: 0, currency: 'TWD' },
  specialProducts: [],
  signatureDrinks: [],
  story: '',
  logo: '',
  images: [],
  isPublic: false,
  status: 'pending',
  notes: '',
})

// ── 詳情面板（唯讀） ─────────────────────────────────────────────
const PartnerDetailPanel: React.FC<{ partner: Partner; onClose: () => void; onEdit: () => void }> = ({ partner, onClose, onEdit }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
    <div className="bg-gray-900 border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
        <div>
          <h2 className="text-xl font-light tracking-wider text-white">{partner.companyName}</h2>
          <p className="text-xs text-gray-500 mt-1">{partner.slug && `/${partner.slug}`}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={onEdit} className="px-4 py-2 bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors">
            編輯
          </button>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded transition-colors">
            <X size={18} className="text-gray-400" />
          </button>
        </div>
      </div>
      <div className="p-6 space-y-6">
        {/* 基本資訊 */}
        <section>
          <h3 className="text-xs tracking-[0.2em] text-gray-500 uppercase mb-3">基本資訊</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-gray-500">聯絡人：</span><span className="text-gray-200">{partner.contactName}</span></div>
            <div><span className="text-gray-500">類型：</span><span className="text-gray-200">{TYPE_LABELS[partner.type]}</span></div>
            <div><span className="text-gray-500">Email：</span><span className="text-gray-200">{partner.email}</span></div>
            <div><span className="text-gray-500">電話：</span><span className="text-gray-200">{partner.phone || '—'}</span></div>
            <div><span className="text-gray-500">統編：</span><span className="text-gray-200">{partner.taxId || '—'}</span></div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">狀態：</span>
              <span className={`px-2 py-0.5 text-xs text-white ${STATUS_COLORS[partner.status]}`}>
                {STATUS_LABELS[partner.status]}
              </span>
            </div>
            <div className="col-span-2"><span className="text-gray-500">地址：</span><span className="text-gray-200">{partner.address?.full || '—'}</span></div>
          </div>
        </section>

        {/* 信用條件 */}
        {partner.creditTerms && (
          <section>
            <h3 className="text-xs tracking-[0.2em] text-gray-500 uppercase mb-3">信用條件</h3>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><span className="text-gray-500">付款條件：</span><span className="text-gray-200">{partner.creditTerms.paymentTerms || '—'}</span></div>
              <div><span className="text-gray-500">信用額度：</span><span className="text-gray-200">{partner.creditTerms.creditLimit?.toLocaleString() || 0} {partner.creditTerms.currency}</span></div>
            </div>
          </section>
        )}

        {/* 品牌故事 */}
        {partner.story && (
          <section>
            <h3 className="text-xs tracking-[0.2em] text-gray-500 uppercase mb-3">品牌故事</h3>
            <p className="text-sm text-gray-300 leading-relaxed">{partner.story}</p>
          </section>
        )}

        {/* 招牌飲品 */}
        {partner.signatureDrinks && partner.signatureDrinks.length > 0 && (
          <section>
            <h3 className="text-xs tracking-[0.2em] text-gray-500 uppercase mb-3">招牌飲品</h3>
            <div className="space-y-2">
              {partner.signatureDrinks.map((d, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Star size={12} className="text-yellow-500 flex-shrink-0" />
                  <span className="text-gray-200">{d.name}</span>
                  {d.description && <span className="text-gray-500">— {d.description}</span>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 圖片 */}
        {((partner.images && partner.images.length > 0) || partner.logo) && (
          <section>
            <h3 className="text-xs tracking-[0.2em] text-gray-500 uppercase mb-3">圖片</h3>
            <div className="flex flex-wrap gap-3">
              {partner.logo && (
                <div className="relative">
                  <img src={partner.logo} alt="logo" className="w-20 h-20 object-cover border border-gray-700" />
                  <span className="absolute bottom-0 left-0 right-0 text-center text-[10px] bg-black/60 text-gray-300">Logo</span>
                </div>
              )}
              {partner.images?.map((img, i) => (
                <img key={i} src={img} alt={`img-${i}`} className="w-20 h-20 object-cover border border-gray-700" />
              ))}
            </div>
          </section>
        )}

        {/* 前台展示狀態 */}
        <section className="flex items-center gap-3 p-4 bg-gray-800 border border-gray-700">
          {partner.isPublic ? (
            <Eye size={16} className="text-green-400" />
          ) : (
            <EyeOff size={16} className="text-gray-500" />
          )}
          <span className="text-sm text-gray-300">
            {partner.isPublic ? '已在前台 /zh/wholesale 公開展示' : '未公開（不顯示於前台）'}
          </span>
        </section>

        {partner.notes && (
          <section>
            <h3 className="text-xs tracking-[0.2em] text-gray-500 uppercase mb-2">備註</h3>
            <p className="text-sm text-gray-400">{partner.notes}</p>
          </section>
        )}
      </div>
    </div>
  </div>
)

// ── 編輯/新增表單 ─────────────────────────────────────────────────
const PartnerForm: React.FC<{
  initial: Partial<Partner>
  onSave: (data: Partial<Partner>) => Promise<void>
  onClose: () => void
  isNew: boolean
}> = ({ initial, onSave, onClose, isNew }) => {
  const [form, setForm] = useState<Partial<Partner>>(initial)
  const [saving, setSaving] = useState(false)
  const [drinkInput, setDrinkInput] = useState('')
  const [drinkDesc, setDrinkDesc] = useState('')
  const [drinkInputEn, setDrinkInputEn] = useState('')
  const [drinkDescEn, setDrinkDescEn] = useState('')
  const [imageInput, setImageInput] = useState('')
  const [expandedSections, setExpandedSections] = useState({ basic: true, credit: false, story: false, storyEn: false, media: false, drinks: false, drinksEn: false })

  const set = (path: string, value: any) => {
    setForm(prev => {
      const parts = path.split('.')
      if (parts.length === 1) return { ...prev, [path]: value }
      const nested = { ...(prev[parts[0] as keyof Partner] as any) }
      nested[parts[1]] = value
      return { ...prev, [parts[0]]: nested }
    })
  }

  const toggle = (section: keyof typeof expandedSections) =>
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))

  const addDrink = () => {
    if (!drinkInput.trim()) return
    setForm(prev => ({
      ...prev,
      signatureDrinks: [...(prev.signatureDrinks || []), { name: drinkInput.trim(), description: drinkDesc.trim() || undefined }]
    }))
    setDrinkInput('')
    setDrinkDesc('')
  }

  const removeDrink = (i: number) =>
    setForm(prev => ({ ...prev, signatureDrinks: prev.signatureDrinks?.filter((_, idx) => idx !== i) }))

  const addDrinkEn = () => {
    if (!drinkInputEn.trim()) return
    setForm(prev => ({
      ...prev,
      signatureDrinksEn: [...(prev.signatureDrinksEn || []), { name: drinkInputEn.trim(), description: drinkDescEn.trim() || undefined }]
    }))
    setDrinkInputEn('')
    setDrinkDescEn('')
  }

  const removeDrinkEn = (i: number) =>
    setForm(prev => ({ ...prev, signatureDrinksEn: prev.signatureDrinksEn?.filter((_, idx) => idx !== i) }))

  const addImage = () => {
    if (!imageInput.trim()) return
    setForm(prev => ({ ...prev, images: [...(prev.images || []), imageInput.trim()] }))
    setImageInput('')
  }

  const removeImage = (i: number) =>
    setForm(prev => ({ ...prev, images: prev.images?.filter((_, idx) => idx !== i) }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await onSave(form)
    } finally {
      setSaving(false)
    }
  }

  const Section: React.FC<{ id: keyof typeof expandedSections; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
    <div className="border border-gray-700">
      <button
        type="button"
        onClick={() => toggle(id)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-800 hover:bg-gray-750 transition-colors"
      >
        <span className="text-xs tracking-[0.2em] text-gray-400 uppercase">{title}</span>
        {expandedSections[id] ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
      </button>
      {expandedSections[id] && <div className="p-4 space-y-4">{children}</div>}
    </div>
  )

  const Field: React.FC<{ label: string; children: React.ReactNode; half?: boolean }> = ({ label, children, half }) => (
    <div className={half ? '' : 'col-span-2'}>
      <label className="block text-xs text-gray-500 mb-1 tracking-wide">{label}</label>
      {children}
    </div>
  )

  const inputClass = "w-full bg-gray-900 border border-gray-700 text-gray-200 text-sm px-3 py-2 focus:outline-none focus:border-gray-500 transition-colors"
  const selectClass = "w-full bg-gray-900 border border-gray-700 text-gray-200 text-sm px-3 py-2 focus:outline-none focus:border-gray-500"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-gray-900 border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 sticky top-0 bg-gray-900 z-10">
          <h2 className="text-lg font-light tracking-wider text-white">
            {isNew ? '新增合作夥伴' : '編輯合作夥伴'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded transition-colors">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* 基本資訊 */}
          <Section id="basic" title="基本資訊">
            <div className="grid grid-cols-2 gap-4">
              <Field label="公司/店名（中）*" >
                <input required className={inputClass} value={form.companyName || ''} onChange={e => set('companyName', e.target.value)} placeholder="綠洲咖啡實驗室" />
              </Field>
              <Field label="Company Name (EN)" >
                <input className={inputClass} value={form.companyNameEn || ''} onChange={e => set('companyNameEn', e.target.value)} placeholder="Oasis Coffee Lab" />
              </Field>
              <Field label="Slug（URL）" half>
                <input className={inputClass} value={form.slug || ''} onChange={e => set('slug', e.target.value)} placeholder="oasis-coffee-lab" />
              </Field>
              <Field label="聯絡人 *" half>
                <input required className={inputClass} value={form.contactName || ''} onChange={e => set('contactName', e.target.value)} />
              </Field>
              <Field label="Email *" half>
                <input required type="email" className={inputClass} value={form.email || ''} onChange={e => set('email', e.target.value)} />
              </Field>
              <Field label="電話" half>
                <input className={inputClass} value={form.phone || ''} onChange={e => set('phone', e.target.value)} />
              </Field>
              <Field label="統一編號" half>
                <input className={inputClass} value={form.taxId || ''} onChange={e => set('taxId', e.target.value)} />
              </Field>
              <Field label="合作類型" half>
                <select className={selectClass} value={form.type || 'wholesale'} onChange={e => set('type', e.target.value)}>
                  {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label="合作狀態" half>
                <select className={selectClass} value={form.status || 'pending'} onChange={e => set('status', e.target.value)}>
                  {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label="完整地址">
                <input className={inputClass} value={form.address?.full || ''} onChange={e => set('address.full', e.target.value)} placeholder="台北市信義區..." />
              </Field>
            </div>

            {/* 前台公開 */}
            <label className="flex items-center gap-3 cursor-pointer mt-2">
              <div
                onClick={() => set('isPublic', !form.isPublic)}
                className={`w-10 h-5 rounded-full transition-colors ${form.isPublic ? 'bg-green-500' : 'bg-gray-600'} relative`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.isPublic ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
              <span className="text-sm text-gray-300">公開展示於前台 <span className="text-gray-500">（/zh/wholesale 合作店家輪播）</span></span>
            </label>
          </Section>

          {/* 信用條件 */}
          <Section id="credit" title="信用條件（B2B）">
            <div className="grid grid-cols-3 gap-4">
              <Field label="付款條件" half>
                <select className={selectClass} value={form.creditTerms?.paymentTerms || 'NET30'} onChange={e => set('creditTerms.paymentTerms', e.target.value)}>
                  {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="信用額度" half>
                <input type="number" className={inputClass} value={form.creditTerms?.creditLimit || 0} onChange={e => set('creditTerms.creditLimit', Number(e.target.value))} />
              </Field>
              <Field label="幣別" half>
                <select className={selectClass} value={form.creditTerms?.currency || 'TWD'} onChange={e => set('creditTerms.currency', e.target.value)}>
                  {['TWD', 'USD', 'CNY'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
          </Section>

          {/* 品牌故事（中文） */}
          <Section id="story" title="品牌故事（中文）">
            <textarea
              className={`${inputClass} h-28 resize-none`}
              value={form.story || ''}
              onChange={e => set('story', e.target.value)}
              placeholder="這間店的故事、他們如何使用 ONGU 的豆子..."
            />
          </Section>

          {/* 品牌故事（英文） */}
          <Section id="storyEn" title="Brand Story (English)">
            <textarea
              className={`${inputClass} h-28 resize-none`}
              value={form.storyEn || ''}
              onChange={e => set('storyEn', e.target.value)}
              placeholder="The story of this shop, how they use ONGU beans..."
            />
            <p className="text-xs text-gray-600 mt-1">→ 顯示於 /en/wholesale 英文版前台</p>
          </Section>

          {/* 招牌飲品（中文） */}
          <Section id="drinks" title="招牌飲品（中文）">
            <div className="space-y-2 mb-3">
              {(form.signatureDrinks || []).map((d, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-800 px-3 py-2">
                  <Star size={12} className="text-yellow-500 flex-shrink-0" />
                  <span className="text-sm text-gray-200 flex-1">{d.name}</span>
                  {d.description && <span className="text-xs text-gray-500">{d.description}</span>}
                  <button type="button" onClick={() => removeDrink(i)} className="text-gray-600 hover:text-red-400 transition-colors"><X size={14} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input className={`${inputClass} flex-1`} placeholder="飲品名稱" value={drinkInput} onChange={e => setDrinkInput(e.target.value)} />
              <input className={`${inputClass} flex-1`} placeholder="簡述（選填）" value={drinkDesc} onChange={e => setDrinkDesc(e.target.value)} />
              <button type="button" onClick={addDrink} className="px-3 py-2 bg-gray-700 text-white text-sm hover:bg-gray-600 transition-colors flex-shrink-0">
                <Plus size={14} />
              </button>
            </div>
          </Section>

          {/* 招牌飲品（英文） */}
          <Section id="drinksEn" title="Signature Drinks (English)">
            <div className="space-y-2 mb-3">
              {(form.signatureDrinksEn || []).map((d, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-800 px-3 py-2">
                  <Star size={12} className="text-yellow-500 flex-shrink-0" />
                  <span className="text-sm text-gray-200 flex-1">{d.name}</span>
                  {d.description && <span className="text-xs text-gray-500">{d.description}</span>}
                  <button type="button" onClick={() => removeDrinkEn(i)} className="text-gray-600 hover:text-red-400 transition-colors"><X size={14} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input className={`${inputClass} flex-1`} placeholder="Drink name" value={drinkInputEn} onChange={e => setDrinkInputEn(e.target.value)} />
              <input className={`${inputClass} flex-1`} placeholder="Description (optional)" value={drinkDescEn} onChange={e => setDrinkDescEn(e.target.value)} />
              <button type="button" onClick={addDrinkEn} className="px-3 py-2 bg-gray-700 text-white text-sm hover:bg-gray-600 transition-colors flex-shrink-0">
                <Plus size={14} />
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-1">→ 顯示於 /en/wholesale 英文版前台</p>
          </Section>

          {/* 圖片與媒體 */}
          <Section id="media" title="圖片與媒體">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Logo URL</label>
              <input className={inputClass} value={form.logo || ''} onChange={e => set('logo', e.target.value)} placeholder="https://..." />
              {form.logo && <img src={form.logo} alt="logo preview" className="mt-2 w-16 h-16 object-cover border border-gray-700" />}
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">照片（可多張，輸入 URL 後按 +）</label>
              <div className="flex gap-2 mb-2">
                <input className={`${inputClass} flex-1`} placeholder="https://..." value={imageInput} onChange={e => setImageInput(e.target.value)} />
                <button type="button" onClick={addImage} className="px-3 py-2 bg-gray-700 text-white text-sm hover:bg-gray-600 transition-colors">
                  <Plus size={14} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(form.images || []).map((img, i) => (
                  <div key={i} className="relative group">
                    <img src={img} alt={`img-${i}`} className="w-16 h-16 object-cover border border-gray-700" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={10} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          {/* 備註 */}
          <div>
            <label className="block text-xs text-gray-500 mb-1 tracking-wide">內部備註</label>
            <textarea
              className={`${inputClass} h-16 resize-none`}
              value={form.notes || ''}
              onChange={e => set('notes', e.target.value)}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="px-5 py-2 border border-gray-700 text-gray-400 text-sm hover:bg-gray-800 transition-colors">
              取消
            </button>
            <button type="submit" disabled={saving} className="px-5 py-2 bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? '儲存中...' : '儲存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── 主元件 ────────────────────────────────────────────────────────
const PartnerManager: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterPublic, setFilterPublic] = useState<string>('all')

  const [viewPartner, setViewPartner] = useState<Partner | null>(null)
  const [editPartner, setEditPartner] = useState<Partial<Partner> | null>(null)
  const [isNew, setIsNew] = useState(false)

  const fetchPartners = useCallback(async () => {
    setLoading(true)
    try {
      const result = await lumi.entities.partners.list({ sort: { createdAt: -1 }, limit: 200 })
      setPartners(result?.list || [])
    } catch (e) {
      console.error('Failed to fetch partners:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchPartners() }, [fetchPartners])

  const handleCreate = async (data: Partial<Partner>) => {
    const now = new Date().toISOString()
    await lumi.entities.partners.create({
      ...data,
      createdAt: now,
      updatedAt: now,
      creator: lumi.auth.user?.userId || 'admin',
    })
    setEditPartner(null)
    await fetchPartners()
  }

  const handleUpdate = async (data: Partial<Partner>) => {
    if (!data._id) return
    const { _id, ...rest } = data
    await lumi.entities.partners.update(_id, { ...rest, updatedAt: new Date().toISOString() })
    setEditPartner(null)
    setViewPartner(null)
    await fetchPartners()
  }

  const handleDelete = async (partner: Partner) => {
    if (!confirm(`確定刪除「${partner.companyName}」？此動作無法復原。`)) return
    await lumi.entities.partners.delete(partner._id)
    await fetchPartners()
  }

  const handleTogglePublic = async (partner: Partner) => {
    await lumi.entities.partners.update(partner._id, { isPublic: !partner.isPublic, updatedAt: new Date().toISOString() })
    await fetchPartners()
  }

  const handleToggleStatus = async (partner: Partner) => {
    const next = partner.status === 'active' ? 'inactive' : 'active'
    await lumi.entities.partners.update(partner._id, { status: next, updatedAt: new Date().toISOString() })
    await fetchPartners()
  }

  // 過濾
  const filtered = partners.filter(p => {
    const q = search.toLowerCase()
    const matchSearch = !q || p.companyName?.toLowerCase().includes(q) || p.contactName?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q)
    const matchStatus = filterStatus === 'all' || p.status === filterStatus
    const matchType = filterType === 'all' || p.type === filterType
    const matchPublic = filterPublic === 'all' || (filterPublic === 'public' ? p.isPublic : !p.isPublic)
    return matchSearch && matchStatus && matchType && matchPublic
  })

  const publicCount = partners.filter(p => p.isPublic && p.status === 'active').length
  const activeCount = partners.filter(p => p.status === 'active').length

  return (
    <div className="p-6 min-h-screen text-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-light tracking-wider mb-1">合作夥伴管理</h1>
          <p className="text-sm text-gray-500">
            共 {partners.length} 筆｜合作中 {activeCount}｜前台展示 {publicCount}
            <span className="ml-3 text-gray-600">→ 前台：<span className="text-gray-400">/zh/wholesale 合作店家輪播</span></span>
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={fetchPartners} className="p-2 hover:bg-gray-800 rounded transition-colors text-gray-400" title="重新整理">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => { setIsNew(true); setEditPartner(emptyForm()) }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors"
          >
            <Plus size={16} />新增夥伴
          </button>
        </div>
      </div>

      {/* 篩選列 */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="搜尋公司名、聯絡人、Email..."
            className="w-full pl-9 pr-3 py-2 bg-gray-800 border border-gray-700 text-sm text-gray-200 focus:outline-none focus:border-gray-500"
          />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-gray-800 border border-gray-700 text-sm text-gray-300 px-3 py-2 focus:outline-none">
          <option value="all">所有狀態</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className="bg-gray-800 border border-gray-700 text-sm text-gray-300 px-3 py-2 focus:outline-none">
          <option value="all">所有類型</option>
          {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filterPublic} onChange={e => setFilterPublic(e.target.value)} className="bg-gray-800 border border-gray-700 text-sm text-gray-300 px-3 py-2 focus:outline-none">
          <option value="all">前台：全部</option>
          <option value="public">已公開</option>
          <option value="hidden">未公開</option>
        </select>
      </div>

      {/* 前台提示說明 */}
      <div className="mb-6 p-3 bg-gray-800 border-l-4 border-blue-500 text-xs text-gray-400">
        <span className="text-blue-400 font-medium">前台連動說明：</span>
        狀態為「合作中」且「已公開」的夥伴，會自動顯示於
        <span className="text-white mx-1">/zh/wholesale</span>
        的「合作店家與品牌夥伴」輪播區。可展示：照片、品牌故事、招牌飲品、聯絡資訊。
      </div>

      {/* 列表 */}
      {loading ? (
        <div className="flex justify-center py-16">
          <RefreshCw size={24} className="text-gray-600 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-600">
          <p className="text-lg mb-2">尚無資料</p>
          <p className="text-sm">點擊「新增夥伴」開始建立第一筆合作夥伴</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* 表頭 */}
          <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_auto_auto_auto] gap-4 px-4 py-2 text-xs text-gray-600 uppercase tracking-wider border-b border-gray-800">
            <span>公司 / 聯絡人</span>
            <span>類型</span>
            <span>狀態</span>
            <span>前台展示</span>
            <span>付款條件</span>
            <span className="text-right">操作</span>
          </div>

          {filtered.map(partner => (
            <div key={partner._id} className="grid md:grid-cols-[2fr_1fr_1fr_auto_auto_auto] gap-4 items-center px-4 py-4 bg-gray-800 border border-gray-700 hover:border-gray-600 transition-colors">
              {/* 公司 */}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {partner.logo && <img src={partner.logo} alt="" className="w-8 h-8 object-cover rounded flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-light text-white truncate">{partner.companyName}</p>
                    <p className="text-xs text-gray-500 truncate">{partner.contactName} · {partner.email}</p>
                  </div>
                </div>
              </div>

              {/* 類型 */}
              <div className="text-xs text-gray-400">{TYPE_LABELS[partner.type] || partner.type}</div>

              {/* 狀態 */}
              <div>
                <button
                  onClick={() => handleToggleStatus(partner)}
                  className={`px-2 py-1 text-xs text-white ${STATUS_COLORS[partner.status]} hover:opacity-80 transition-opacity`}
                  title="點擊切換 合作中/停用"
                >
                  {STATUS_LABELS[partner.status]}
                </button>
              </div>

              {/* 前台展示開關 */}
              <div className="flex justify-center">
                <button
                  onClick={() => handleTogglePublic(partner)}
                  title={partner.isPublic ? '點擊隱藏' : '點擊公開至前台'}
                  className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${partner.isPublic ? 'text-green-400 hover:text-green-300' : 'text-gray-600 hover:text-gray-400'}`}
                >
                  {partner.isPublic ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>

              {/* 付款條件 */}
              <div className="text-xs text-gray-500">{partner.creditTerms?.paymentTerms || '—'}</div>

              {/* 操作按鈕 */}
              <div className="flex items-center gap-1 justify-end">
                <button
                  onClick={() => setViewPartner(partner)}
                  className="p-2 hover:bg-gray-700 rounded transition-colors text-gray-500 hover:text-gray-300"
                  title="查看詳情"
                >
                  <Globe size={14} />
                </button>
                <button
                  onClick={() => { setIsNew(false); setEditPartner({ ...partner }) }}
                  className="p-2 hover:bg-gray-700 rounded transition-colors text-gray-500 hover:text-blue-400"
                  title="編輯"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={() => handleDelete(partner)}
                  className="p-2 hover:bg-gray-700 rounded transition-colors text-gray-500 hover:text-red-400"
                  title="刪除"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 詳情面板 */}
      {viewPartner && (
        <PartnerDetailPanel
          partner={viewPartner}
          onClose={() => setViewPartner(null)}
          onEdit={() => {
            setIsNew(false)
            setEditPartner({ ...viewPartner })
            setViewPartner(null)
          }}
        />
      )}

      {/* 新增/編輯表單 */}
      {editPartner && (
        <PartnerForm
          initial={editPartner}
          isNew={isNew}
          onClose={() => setEditPartner(null)}
          onSave={isNew ? handleCreate : handleUpdate}
        />
      )}
    </div>
  )
}

export default PartnerManager
